import {Box, capitalize, Checkbox, FormControlLabel, Stack} from "@mui/material";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useLocation, useNavigate, useParams} from "react-router";
import {GridColDef, GridRowParams} from "@mui/x-data-grid";
import {
    DataLoader,
    DataRequest,
    DataRequestState,
    useDataRequest,
} from "../data";
import {useT} from "../../i18n";
import SearchField from "../SearchField";
import {useGetApi} from "../../client";
import {Umsatz} from "../../api";
import DataTable from "../data/DataTable";
import StartEndFilter from "../StartEndFilter";
import {formatAsMoney} from "../../formatterHelpers";
import {formatGermanNumber} from "../../utils/fomatters";
import useCustomFilters from "../data/useCustomFilters";

export type VorgaengeFilter = {
    search?: string;
    filtered?: string;
    baustellenId?: string;
    isTaetigkeitsbericht?: boolean;
    startDate?: string;
    endDate?: string;
};

export type Props = Omit<DataRequest<VorgaengeFilter>, "filter"> &
    Partial<Pick<DataRequest<VorgaengeFilter>, "filter">>;

const tableName: string = "umsaetze"

export default function VorgaengeDataTable({...input}: Props) {
    const {id: baustellenId = ""} = useParams<{
        id: string;
    }>();
    const request = useDataRequest<VorgaengeFilter>({
        filter: {baustellenId: baustellenId},
        ...input,
    });
    const [filteredState, setFilteredState] = useState("");
    return (
        <Stack spacing={2}>
            <FilterComp
                request={request}
                filteredStateFilter={filteredState}
            />
            <VorgaengeResults
                request={request}
                setFilteredState={setFilteredState}
            />
        </Stack>
    );
}

function FilterComp({
                        request,
                        filteredStateFilter,
                    }: {
    request: DataRequestState<VorgaengeFilter>;
    filteredStateFilter: string;
}) {
    const {setFilter} = request;
    const {currentCustomFilter, setCustomField} = useCustomFilters<VorgaengeFilter>({
        tableName: tableName,
        defaultFilter: {
            search: "",
            startDate: "",
            endDate: "",
            baustellenId: request.filter.baustellenId,
            isTaetigkeitsbericht: request.filter.isTaetigkeitsbericht,
        }
    });

    const {t: translation} = useT("taetigkeitsbericht");

    useEffect(() => {
        setFilter({
            search: currentCustomFilter.search,
            startDate: currentCustomFilter.startDate,
            endDate: currentCustomFilter.endDate,
            baustellenId: currentCustomFilter.baustellenId,
            isTaetigkeitsbericht: currentCustomFilter.isTaetigkeitsbericht,
            filtered: filteredStateFilter || undefined,
        });
    }, [currentCustomFilter, filteredStateFilter, setFilter]);

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                width: "100%",
            }}
        >
            <>
                <SearchField
                    value={currentCustomFilter.search}
                    onChange={(val) => setCustomField("search", val)}
                />
                <StartEndFilter startDatum={currentCustomFilter.startDate}
                                endeDatum={currentCustomFilter.endDate}
                                setCustomField={setCustomField}
                />
                <FormControlLabel
                    sx={{ml: 2}}
                    control={
                        <Checkbox
                            color="primary"
                            checked={currentCustomFilter.isTaetigkeitsbericht}
                            onChange={(event) => setCustomField("isTaetigkeitsbericht", event.target.checked)}
                        />
                    }
                    label={capitalize(translation("only-activity-reports"))}
                />
            </>
        </Box>
    );
}

function VorgaengeResults({
                              request,
                              setFilteredState,
                          }: {
    request: DataRequestState<VorgaengeFilter>;
    setFilteredState: (value: string) => void;
}) {
    return <Results request={request} setFilteredState={setFilteredState}/>;
}

function Results({
                     request,
                     setFilteredState,
                 }: {
    request: DataRequestState<VorgaengeFilter>;
    setFilteredState: (value: string) => void;
}) {
    const navigate = useNavigate();
    const getApi = useGetApi();
    const loadUmsatz: DataLoader<VorgaengeFilter, Umsatz> = useCallback(
        async (params) => (await getApi()).umsatz.sucheUmsatz({...params}),
        [getApi]
    );
    const location = useLocation();
    return (
        <DataTable
            columns={useColumns()}
            request={request}
            queryKey={[tableName]}
            loadData={loadUmsatz}
            onRowClick={(row: GridRowParams<Umsatz>, event) => {
                if (event.ctrlKey || event.metaKey) {
                    window.open(`${row.row.referenzUrl}`, "_blank");
                } else {
                    navigate(`${row.row.referenzUrl}`, {
                        state: {from: location.pathname + location.search},
                    });
                }
            }}
            name={`${tableName}-${request.filter?.baustellenId}`}
            setFiltered={setFilteredState}
        />
    );
}

function useColumns(): Array<GridColDef<Umsatz>> {
    const {t: translation} = useT("vorgaenge");

    return useMemo(
        () => [
            {
                field: "buchungsDatum",
                headerName: capitalize(translation("booking-date")),
                flex: 1,
                type: "date",
                valueGetter: (value) => {
                    return new Date(value);
                },
                renderCell: ({value}) =>
                    new Date(value as string).toLocaleDateString(undefined, {
                        month: "2-digit",
                        day: "2-digit",
                        year: "numeric",
                    }),
            },
            {
                field: "buchungsArt",
                headerName: capitalize(translation("booking-type")),
                flex: 1,
            },
            {
                field: "buchungsTypAngepasst",
                headerName: capitalize(translation("booking-short-type")),
                flex: 1,
            },
            {
                field: "vorgangBezeichnung",
                headerName: capitalize(translation("description")),
                flex: 2,
            },
            {
                field: "materialName",
                headerName: capitalize(translation("material")),
                flex: 2,
            },
            {
                field: "eakSchluessel",
                headerName: capitalize(translation("eak-nr")),
                flex: 1,
            },
            {
                field: "gesamtmengeBerechnet",
                headerName: capitalize(translation("total-units")),
                align: "right",
                headerAlign: "right",
                flex: 1,
                valueFormatter: formatGermanNumber,
            },
            {
                field: "mengeneinheit",
                headerName: capitalize(translation("unit")),
                flex: 1,
            },
            {
                field: "lieferscheinNr",
                headerName: capitalize(translation("delivery-note-number")),
                flex: 2,
            },
            {
                field: "betrag",
                headerName: capitalize(translation("total-amount")),
                flex: 1.5,
                align: "right",
                headerAlign: "right",
                type: "number",
                renderCell: ({value}) => {
                    return formatAsMoney(value);
                },
            },
            {
                field: "bemerkungTb",
                headerName: capitalize(translation("remark")),
                flex: 2,
            },
        ],
        [translation]
    );
}
